import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, Mail, Phone, ArrowRight } from 'lucide-react';
import { CONTACT_INFO } from '../../data/branding';
import { buildAffiliatePath } from '../../lib/affiliate';

/**
 * Lightweight header used across the affiliate program micro-site
 * (`affiliate.axonmuscle.com` and `/affiliate/*` paths).
 *
 * Intentionally separate from the storefront `<Header/>` so the affiliate
 * site has its own identity — no cart, no product nav, just the program.
 */
export const AffiliateHeader = ({ ctaTo = '/apply', ctaLabel = 'Apply Now' }) => (
  <header className="sticky top-0 z-40 bg-neutral-950/95 backdrop-blur-md border-b border-neutral-800">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-16">
        <Link to={buildAffiliatePath('/')} className="flex items-center gap-3 group">
          <div className="w-10 h-10 bg-gradient-to-br from-axon-600 to-axon-500 rounded-xl flex items-center justify-center group-hover:shadow-glow-cyan transition-all">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-display text-base font-bold text-white tracking-wide uppercase">Axon Muscle</span>
            <span className="text-[10px] font-semibold text-axon-500 tracking-[0.2em] uppercase">Affiliate Program</span>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm font-semibold text-neutral-300">
          <Link to={buildAffiliatePath('/')} className="hover:text-white transition-colors">Home</Link>
          <Link to={buildAffiliatePath('/apply')} className="hover:text-white transition-colors">Apply</Link>
          <Link to={buildAffiliatePath('/login')} className="hover:text-white transition-colors">Affiliate Login</Link>
        </nav>
        <Link
          to={buildAffiliatePath(ctaTo)}
          className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 text-white text-sm font-semibold uppercase tracking-wider shadow-button hover:shadow-glow-cyan transition-all"
        >
          {ctaLabel}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </header>
);

export const AffiliateFooter = () => (
  <footer className="border-t border-neutral-800 bg-neutral-950 text-neutral-400 mt-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 bg-gradient-to-br from-axon-600 to-axon-500 rounded-lg flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <span className="font-display text-lg font-bold text-white uppercase tracking-wide">Axon Muscle</span>
        </div>
        <p className="text-sm leading-relaxed">
          Premium muscle supplements & performance nutrition. Become an affiliate and earn from every sale you drive.
        </p>
      </div>
      <div>
        <h4 className="text-white font-semibold uppercase tracking-wider text-xs mb-3">Program</h4>
        <ul className="space-y-2 text-sm">
          <li><Link to={buildAffiliatePath('/')} className="hover:text-axon-400">Overview</Link></li>
          <li><Link to={buildAffiliatePath('/apply')} className="hover:text-axon-400">Apply</Link></li>
          <li><Link to={buildAffiliatePath('/login')} className="hover:text-axon-400">Affiliate Login</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="text-white font-semibold uppercase tracking-wider text-xs mb-3">Contact</h4>
        <ul className="space-y-2 text-sm">
          <li className="flex items-center gap-2">
            <Phone className="w-4 h-4" />
            <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="hover:text-axon-400">{CONTACT_INFO.phone}</a>
          </li>
          <li className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-axon-400">{CONTACT_INFO.email}</a>
          </li>
          <li className="text-xs leading-relaxed pt-2 text-neutral-500">
            {CONTACT_INFO.address.street}<br />
            {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}
          </li>
        </ul>
      </div>
    </div>
    <div className="border-t border-neutral-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-xs text-neutral-500 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
        <span>© {new Date().getFullYear()} Axon Muscle. All rights reserved.</span>
        <span>Premium muscle supplements & performance nutrition.</span>
      </div>
    </div>
  </footer>
);

export default AffiliateHeader;
